import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';

import { LANGUAGE_CODES } from '@/i18n/languages';
import { getLanguageCode } from '@/utils/i18nLocale';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(quarterOfYear);
dayjs.extend(calendar);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);

/** dayjs locale packs for every UI language — loaded on demand. */
const dayjsLocaleLoaders: Record<string, () => Promise<unknown>> = {
  af: () => import('dayjs/locale/af'),
  ar: () => import('dayjs/locale/ar'),
  bg: () => import('dayjs/locale/bg'),
  cs: () => import('dayjs/locale/cs'),
  da: () => import('dayjs/locale/da'),
  de: () => import('dayjs/locale/de'),
  el: () => import('dayjs/locale/el'),
  en: () => import('dayjs/locale/en'),
  es: () => import('dayjs/locale/es'),
  et: () => import('dayjs/locale/et'),
  fi: () => import('dayjs/locale/fi'),
  fr: () => import('dayjs/locale/fr'),
  he: () => import('dayjs/locale/he'),
  hr: () => import('dayjs/locale/hr'),
  hu: () => import('dayjs/locale/hu'),
  id: () => import('dayjs/locale/id'),
  it: () => import('dayjs/locale/it'),
  ja: () => import('dayjs/locale/ja'),
  ka: () => import('dayjs/locale/ka'),
  ko: () => import('dayjs/locale/ko'),
  lt: () => import('dayjs/locale/lt'),
  lv: () => import('dayjs/locale/lv'),
  nb: () => import('dayjs/locale/nb'),
  nl: () => import('dayjs/locale/nl'),
  pl: () => import('dayjs/locale/pl'),
  pt: () => import('dayjs/locale/pt'),
  ro: () => import('dayjs/locale/ro'),
  ru: () => import('dayjs/locale/ru'),
  sk: () => import('dayjs/locale/sk'),
  sl: () => import('dayjs/locale/sl'),
  sr: () => import('dayjs/locale/sr'),
  sv: () => import('dayjs/locale/sv'),
  th: () => import('dayjs/locale/th'),
  tr: () => import('dayjs/locale/tr'),
  uk: () => import('dayjs/locale/uk'),
  vi: () => import('dayjs/locale/vi'),
  zh: () => import('dayjs/locale/zh'),
};

const loadedLocales = new Set<string>(['en']);

export const setDayjsLocale = (language?: string): void => {
  const code = getLanguageCode(language);
  const locale = LANGUAGE_CODES.includes(code) ? code : 'en';

  if (locale === 'en') {
    dayjs.locale('en');
    return;
  }

  if (loadedLocales.has(locale)) {
    dayjs.locale(locale);
    return;
  }

  const loader = dayjsLocaleLoaders[locale];
  if (!loader) {
    dayjs.locale('en');
    return;
  }

  void loader().then(() => {
    loadedLocales.add(locale);
    dayjs.locale(locale);
  });
};

setDayjsLocale('en');

export default dayjs;
